import styled from '@emotion/styled';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import React, { Fragment } from 'react';

import WsColors from '../../WsColors';
import { WsTypography, WsTypoVariants } from '../WsTypography';

interface WsDrawerVariant {
  location: 'left' | 'right' | 'bottom';
  size: 'small' | 'compact' | 'medium' | 'large' | 'full';
}

const mapDrawerSize = new Map();
mapDrawerSize.set('small', '30vw');
mapDrawerSize.set('compact', '45vw');
mapDrawerSize.set('medium', '50vw');
mapDrawerSize.set('large', '70vw');
mapDrawerSize.set('full', '100vw');

const DrawerHeader = styled('div')(({ location }: WsDrawerVariant) => ({
  padding: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: location == 'right' ? 'flex-start' : 'flex-end',
  backgroundColor: WsColors.TertiaryShades100,
}));

const StyledBox = styled(Box)(({ location, size }: WsDrawerVariant) => ({
  width: mapDrawerSize.get(size),
  height: location == 'bottom' ? '100px' : '',
}));

export interface WsDrawerProps {
  open: boolean;
  onClose(): void;
  title?: string;
  children: React.ReactNode;
  rightChildren?: React.ReactNode;
  iconType?: 'arrow' | 'cross';
  position?: 'left' | 'right' | 'bottom';
  size?: 'small' | 'medium' | 'large' | 'full' | 'compact';
  headerSize?: 'small' | 'medium' | 'large';
  hideHeader?: boolean;
  style?: React.CSSProperties;
  hideOverflow?: boolean;
  className?: string;
  fullHeight?: boolean;
}

const mapHeaderSize = new Map();
mapHeaderSize.set('small', WsTypoVariants.BODY1);
mapHeaderSize.set('medium', WsTypoVariants.SUB_TITLE);
mapHeaderSize.set('large', WsTypoVariants.TITLE);

export default function WsDrawer({
  open,
  onClose,
  position = 'right',
  title = '',
  children,
  iconType = 'cross',
  size = 'medium',
  headerSize = 'large',
  hideHeader = false,
  style,
  hideOverflow = false,
  className,
  rightChildren,
  fullHeight = false,
}: WsDrawerProps) {
  const handleDrawerClose = () => onClose();

  const WsDrawerHeader = () =>
    !hideHeader ? (
      <DrawerHeader location={position} size={size}>
        {position == 'right' && (
          <Grid container direction="row">
            <Grid container item xs={rightChildren ? 8 : 12} alignItems="center" direction="row">
              <IconButton className="close-drawer" onClick={handleDrawerClose}>
                {iconType == 'arrow' ? <ArrowBackIcon /> : <CloseIcon />}
              </IconButton>
              <WsTypography variant={mapHeaderSize.get(headerSize)}>{title}</WsTypography>
            </Grid>
            {rightChildren && (
              <Grid item xs={4} container alignItems="flex-end" justifyContent={'flex-end'}>
                {rightChildren}
              </Grid>
            )}
          </Grid>
        )}
        {position != 'right' && (
          <Grid item xs={rightChildren ? 8 : 12} alignItems="center" direction="row">
            <WsTypography variant={mapHeaderSize.get(headerSize)}>{title}</WsTypography>
            <IconButton onClick={handleDrawerClose}>
              {iconType == 'arrow' ? <ArrowBackIcon /> : <CloseIcon />}
            </IconButton>
            {rightChildren && (
              <Grid item xs={4} alignItems="flex-end" justifyContent={'flex-end'}>
                {rightChildren}
              </Grid>
            )}
          </Grid>
        )}
      </DrawerHeader>
    ) : null;

  return (
    <Fragment key={position}>
      <Drawer
        anchor={position}
        open={open}
        onClose={handleDrawerClose}
        style={{ zIndex: '1300', ...style }}
        PaperProps={{
          sx: {
            overflow: hideOverflow ? 'clip' : 'auto',
            ...(fullHeight && { width: mapDrawerSize.get(size) }),
          },
        }}
      >
        {fullHeight ? (
          <>
            <WsDrawerHeader />
            <Divider />
            {children}
          </>
        ) : (
          <Box className={className}>
            <WsDrawerHeader />
            <Divider />
            <StyledBox role="presentation" location={position} size={size}>
              {children}
            </StyledBox>
          </Box>
        )}
      </Drawer>
    </Fragment>
  );
}
